import React from 'react';
import { Card } from 'antd';
import { useLocation } from 'react-router-dom';
import { SkBar } from './_primitives';

const PointsConfigHeader = () => (
  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-4 sm:mb-6">
    <div className="flex flex-col gap-2 min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <SkBar h="h-3" w="w-16" />
        <SkBar h="h-3" w="w-2" />
        <SkBar h="h-3" w="w-32" />
      </div>
      <SkBar h="h-7" w="w-72" className="max-w-full mt-1" />
      <SkBar h="h-3" w="w-96" className="max-w-full" />
    </div>
    <SkBar h="h-9" w="w-28" className="rounded-md flex-shrink-0" />
  </div>
);

const TabsRow = ({ active = 0 }) => (
  <div className="flex gap-6 border-b border-gray-200 mb-6 overflow-x-auto">
    {Array.from({ length: 2 }).map((_, i) => (
      <div
        key={i}
        className={`pb-2 flex items-center gap-2 flex-shrink-0 ${
          active === i ? 'border-b-2 border-gray-300' : ''
        }`}
      >
        <div className="w-4 h-4 rounded bg-gray-200 animate-pulse" />
        <SkBar h="h-4" w="w-32" />
      </div>
    ))}
  </div>
);

const SectionTitle = () => (
  <div className="flex flex-col gap-1.5 mb-3 mt-2">
    <SkBar h="h-5" w="w-40" />
    <SkBar h="h-3" w="w-64" className="max-w-full" />
  </div>
);

/* ─────────── System Settings tab ─────────── */

const TopStatCard = () => (
  <Card className="shadow-sm" styles={{ body: { padding: 20 } }}>
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0 flex flex-col gap-2">
        <SkBar h="h-3" w="w-28" />
        <SkBar h="h-6" w="w-16" />
      </div>
      <div className="w-10 h-10 rounded-lg bg-gray-200 animate-pulse flex-shrink-0" />
    </div>
  </Card>
);

const SettingCard = () => (
  <Card className="shadow-sm" styles={{ body: { padding: 20 } }}>
    <div className="flex items-start gap-3 mb-4">
      <div className="w-10 h-10 rounded-lg bg-gray-200 animate-pulse flex-shrink-0" />
      <div className="flex-1 min-w-0 flex flex-col gap-2">
        <SkBar h="h-4" w="w-40" />
        <SkBar h="h-3" w="w-full" className="max-w-[280px]" />
      </div>
    </div>
    <div className="flex items-end justify-between gap-3">
      <SkBar h="h-8" w="w-16" />
      <SkBar h="h-6" w="w-6" className="rounded-md" />
    </div>
  </Card>
);

const SystemSettingsTabContent = () => (
  <>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <TopStatCard key={i} />
      ))}
    </div>

    <SectionTitle />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-6">
      <SettingCard />
      <SettingCard />
    </div>

    <SectionTitle />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-6">
      <SettingCard />
    </div>

    <SectionTitle />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-6">
      <SettingCard />
    </div>

    <SectionTitle />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-6">
      <SettingCard />
    </div>
  </>
);

/* ─────────── Daily Role Points tab ─────────── */

const InfoBanner = () => (
  <div className="mb-6 p-4 border border-blue-200 bg-blue-50 rounded-lg flex items-start gap-3">
    <div className="w-6 h-6 rounded-full bg-blue-200 animate-pulse flex-shrink-0 mt-0.5" />
    <div className="flex-1 min-w-0 flex flex-col gap-2">
      <SkBar h="h-4" w="w-40" />
      <SkBar h="h-3" w="w-full" />
      <SkBar h="h-3" w="w-3/4" />
    </div>
  </div>
);

const ROLE_COLS = ['w-12', 'w-20', 'w-20', 'w-20', 'w-14', 'w-14'];

const RoleRow = () => (
  <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr] gap-3 py-4 border-b border-gray-100 last:border-b-0 items-center">
    {/* Role: icon + name */}
    <div className="flex items-center gap-2 min-w-0">
      <div className="w-5 h-5 rounded bg-gray-200 animate-pulse flex-shrink-0" />
      <SkBar h="h-3" w="w-32" />
    </div>
    {/* Points / Day */}
    <SkBar h="h-3" w="w-12" className="justify-self-center" />
    {/* Expires In */}
    <SkBar h="h-3" w="w-16" className="justify-self-center" />
    {/* Grant Days */}
    <SkBar h="h-3" w="w-20" className="justify-self-center" />
    {/* Status switch */}
    <SkBar h="h-6" w="w-12" className="rounded-full justify-self-center" />
    {/* Action icons */}
    <div className="flex items-center gap-2 justify-end">
      <SkBar h="h-8" w="w-8" className="rounded-md" />
      <SkBar h="h-8" w="w-8" className="rounded-md" />
    </div>
  </div>
);

const RoleDailyPointsCard = () => (
  <Card className="shadow-sm" styles={{ body: { padding: 20 } }}>
    <div className="flex items-center justify-between gap-3 mb-4 pb-4 border-b border-gray-100">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-5 h-5 rounded bg-gray-200 animate-pulse flex-shrink-0" />
        <SkBar h="h-5" w="w-48" />
        <SkBar h="h-5" w="w-8" className="rounded-md" />
      </div>
      <SkBar h="h-9" w="w-28" className="rounded-md flex-shrink-0" />
    </div>
    <div className="overflow-x-auto">
      <div className="min-w-[720px]">
        <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr] gap-3 pb-3 border-b border-gray-200">
          {ROLE_COLS.map((w, i) => {
            const isLast = i === ROLE_COLS.length - 1;
            const isFirst = i === 0;
            return (
              <SkBar
                key={i}
                h="h-3"
                w={w}
                className={
                  isLast
                    ? 'justify-self-end'
                    : isFirst
                    ? ''
                    : 'justify-self-center'
                }
              />
            );
          })}
        </div>
        {Array.from({ length: 4 }).map((_, i) => (
          <RoleRow key={i} />
        ))}
      </div>
    </div>
  </Card>
);

const DailyRolePointsTabContent = () => (
  <>
    <InfoBanner />
    <RoleDailyPointsCard />
  </>
);

/* ─────────── Main loader ─────────── */

const PointsConfigPageLoader = () => {
  const { search } = useLocation();
  const tab = new URLSearchParams(search).get('tab');
  const isDailyRole = tab === 'daily-role';

  return (
    <div className="w-full p-3 sm:p-4 md:p-6">
      <PointsConfigHeader />
      <TabsRow active={isDailyRole ? 1 : 0} />
      {isDailyRole ? <DailyRolePointsTabContent /> : <SystemSettingsTabContent />}
    </div>
  );
};

export default PointsConfigPageLoader;
